import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';

const emptyKpis = {
  todayOrdersCount: 0,
  pendingDelivery: 0,
  paidOrdersCount: 0,
  unpaidOrdersCount: 0,
  averagePaidOrderValue: 0,
  cancelledOrders: 0,
  totalRevenuePaid: 0,
  totalRevenueUnpaid: 0,
};

export default function SalesDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState(null);
  const [counts, setCounts] = useState({ pending: 0, confirmed: 0, rejected: 0 });
  const [countsLoading, setCountsLoading] = useState(true);
  const [kpis, setKpis] = useState(emptyKpis);
  const [kpisLoading, setKpisLoading] = useState(true);
  const displayName = user?.name?.split(' ')[0] || 'User';
  const isSalesUser = String(user?.role || '').toLowerCase() === 'custom_role_sales';
  const todaySaudi = React.useMemo(() => {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Riyadh',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).formatToParts(new Date());
      const day = parts.find((p) => p.type === 'day')?.value || '';
      const month = parts.find((p) => p.type === 'month')?.value || '';
      const year = parts.find((p) => p.type === 'year')?.value || '';
      return [day, month, year].filter(Boolean).join('-');
    } catch {
      return '';
    }
  }, []);

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.state?.message, location.pathname]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await apiService.orders.getSalesOrderCounts();
        if (!cancelled && res.data) setCounts(res.data);
      } catch {
        if (!cancelled) setCounts({ pending: 0, confirmed: 0, rejected: 0 });
      } finally {
        if (!cancelled) setCountsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadKpis() {
      try {
        const res = await apiService.orders.getSalesDashboardKpis();
        if (cancelled || !res.data) return;
        setKpis({
          todayOrdersCount: Number(res.data.todayOrdersCount || 0),
          pendingDelivery: Number(res.data.pendingDelivery || 0),
          paidOrdersCount: Number(res.data.paidOrdersCount || 0),
          unpaidOrdersCount: Number(res.data.unpaidOrdersCount || 0),
          averagePaidOrderValue: Number(res.data.averagePaidOrderValue || 0),
          cancelledOrders: Number(res.data.cancelledOrders || 0),
          totalRevenuePaid: Number(res.data.totalRevenuePaid || 0),
          totalRevenueUnpaid: Number(res.data.totalRevenueUnpaid || 0),
        });
      } catch {
        if (!cancelled) setKpis(emptyKpis);
      } finally {
        if (!cancelled) setKpisLoading(false);
      }
    }
    loadKpis();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="p-3 sm:p-4 md:p-6 pb-6 sm:pb-8">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-5 md:p-6">
        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-800 rounded-lg text-sm sm:text-base">
            {successMessage}
          </div>
        )}
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-1">Hello, {displayName}!</h1>
        {!isSalesUser && (
          <p className="text-sm sm:text-base text-gray-600 mb-6">Today&apos;s bike sales (Saudi time).</p>
        )}

        {!isSalesUser && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-8">
            <button
              type="button"
              onClick={() => navigate('/sales/customers')}
              className="min-h-[44px] sm:min-h-0 bg-slate-200 hover:bg-slate-300 active:bg-slate-400 text-gray-800 rounded-xl py-4 px-4 sm:py-4 sm:px-3 flex flex-col items-center justify-center border border-slate-300 transition-colors touch-manipulation"
            >
              <span className="text-2xl sm:text-2xl mb-2">📇</span>
              <span className="text-sm sm:text-sm font-semibold">Customers</span>
              <span className="text-xs mt-1 text-gray-600 text-center">List &amp; add customers</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/sales/orders')}
              className="min-h-[44px] sm:min-h-0 bg-orange-200 hover:bg-orange-300 active:bg-orange-400 text-gray-800 rounded-xl py-4 px-4 sm:py-4 sm:px-3 flex flex-col items-center justify-center border border-orange-300 transition-colors touch-manipulation"
            >
              <span className="text-2xl sm:text-2xl mb-2">🕐</span>
              <span className="text-sm sm:text-sm font-semibold">Pending Orders</span>
              {!countsLoading && counts.pending > 0 && (
                <span className="text-xs mt-1 text-gray-600 text-center">
                  {counts.pending} {counts.pending === 1 ? 'order' : 'orders'} awaiting confirmation
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/sales/menu')}
              className="min-h-[44px] sm:min-h-0 bg-green-200 hover:bg-green-300 active:bg-green-400 text-gray-800 rounded-xl py-4 px-4 sm:py-4 sm:px-3 flex flex-col items-center justify-center border border-green-300 transition-colors touch-manipulation"
            >
              <span className="text-2xl sm:text-2xl mb-2">➕</span>
              <span className="text-sm sm:text-sm font-semibold">New Order</span>
            </button>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex flex-col">
              <div className="text-sm font-semibold text-gray-700">Today&apos;s dashboard</div>
              {todaySaudi && (
                <div className="text-xs text-gray-600 mt-0.5 font-medium tabular-nums">
                  Date : {todaySaudi}
                </div>
              )}
            </div>
            {!isSalesUser && (
              <button
                type="button"
                onClick={() => navigate('/sales/revenue')}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                View revenue
              </button>
            )}
          </div>

          {kpisLoading ? (
            <div className="py-6 text-center text-gray-500">Loading…</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <button
                type="button"
                onClick={() => navigate('/sales/orders')}
                className="bg-gray-50 border rounded-lg p-3 text-left hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
              >
                <div className="text-xs text-gray-500">Today&apos;s orders</div>
                <div className="text-lg font-bold text-gray-800 tabular-nums">{kpis.todayOrdersCount}</div>
              </button>
              <button
                type="button"
                onClick={() => navigate('/sales/orders')}
                className="bg-gray-50 border rounded-lg p-3 text-left hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
              >
                <div className="text-xs text-gray-500">Pending delivery</div>
                <div className="text-lg font-bold text-indigo-800 tabular-nums">{kpis.pendingDelivery}</div>
              </button>
              <button
                type="button"
                onClick={() => navigate('/sales/orders')}
                className="bg-gray-50 border rounded-lg p-3 text-left hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
              >
                <div className="text-xs text-gray-500">Paid orders</div>
                <div className="text-lg font-bold text-emerald-800 tabular-nums">{kpis.paidOrdersCount}</div>
              </button>
              <button
                type="button"
                onClick={() => navigate('/sales/orders')}
                className="bg-gray-50 border rounded-lg p-3 text-left hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
              >
                <div className="text-xs text-gray-500">Unpaid orders</div>
                <div className="text-lg font-bold text-amber-800 tabular-nums">{kpis.unpaidOrdersCount}</div>
              </button>
              <button
                type="button"
                onClick={() => navigate('/sales/orders')}
                className="bg-gray-50 border rounded-lg p-3 text-left hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
              >
                <div className="text-xs text-gray-500">Avg. paid order</div>
                <div className="text-lg font-bold text-gray-800 tabular-nums">
                  SAR {Number(kpis.averagePaidOrderValue).toFixed(2)}
                </div>
              </button>
              <button
                type="button"
                onClick={() => navigate('/sales/orders')}
                className="bg-gray-50 border rounded-lg p-3 text-left hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
              >
                <div className="text-xs text-gray-500">Cancelled orders</div>
                <div className="text-lg font-bold text-red-800 tabular-nums">{kpis.cancelledOrders}</div>
              </button>
              <button
                type="button"
                onClick={() => navigate('/sales/orders')}
                className="bg-gray-50 border rounded-lg p-3 col-span-2 sm:col-span-1 text-left hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
              >
                <div className="text-xs text-gray-500">Total revenue (paid)</div>
                <div className="text-lg font-bold text-green-800 tabular-nums">
                  SAR {Number(kpis.totalRevenuePaid).toFixed(2)}
                </div>
              </button>
              <button
                type="button"
                onClick={() => navigate('/sales/orders')}
                className="bg-gray-50 border rounded-lg p-3 col-span-2 sm:col-span-1 text-left hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
              >
                <div className="text-xs text-gray-500">Total revenue (unpaid)</div>
                <div className="text-lg font-bold text-amber-900 tabular-nums">
                  SAR {Number(kpis.totalRevenueUnpaid).toFixed(2)}
                </div>
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => navigate('/sales/menu')}
            className="min-h-[48px] rounded-xl px-4 py-4 text-left border border-emerald-200 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 active:from-emerald-700 active:to-green-700 text-white shadow-sm transition-colors"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">New order</div>
                <div className="text-xs text-emerald-50/90 mt-0.5">Create a new sales order</div>
              </div>
              <div className="text-2xl">➕</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => navigate('/sales/customers/create')}
            className="min-h-[48px] rounded-xl px-4 py-4 text-left border border-indigo-200 bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 active:from-indigo-700 active:to-violet-700 text-white shadow-sm transition-colors"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">New customer</div>
                <div className="text-xs text-indigo-50/90 mt-0.5">Add a customer profile</div>
              </div>
              <div className="text-2xl">👤</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
